import MenuBook from "@mui/icons-material/MenuBook";
import { Typography } from "@mui/material";
import { IssueCount } from "components/IssueCount";
import { RoleNamesEnum } from "encamp-shared/src/constants/permissionsTypes";
import { Issue, Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import pluralize from "pluralize";
import React, { useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useIgnoredIssues } from "routes/Customer/Chemicals/Report/useReport";
import { anySegment } from "util/issues";

import { BaseLink } from "./BaseLink";

interface CatalogLinkProps {
  id: string;
  type: keyof typeof modelToSchemas;
  name: string;
  issues: Issue[];
}

// This maps the model type to the issue schemas that are relevant to the
// model. Only catalog-level schemas belong here: facility-scoped issues
// (e.g. FacilityChemical) are fixed on the inventory itself, not in the
// catalog, so counting them would send the user to the wrong place.
const modelToSchemas = {
  Chemical: ["Chemical", "ChemicalComponent", "ChemicalStateField"],
  Product: ["Product", "ProductChemical"],
};

export const CatalogLink: React.FC<CatalogLinkProps> = ({ type, id, name, issues }) => {
  const ignoredIssues = useIgnoredIssues();
  const { hasPermissions, getUserRole, loading: authorizationLoading } = useAuthorization();
  const hasChemicalCatalogPermission = hasPermissions([Permission.RouteEpcraProducts]);

  // Check if user is EHS Field User based on their role name
  const userRole = getUserRole();
  const isEHSFieldUser =
    userRole === RoleNamesEnum.encampEHSFieldUser ||
    userRole === RoleNamesEnum.encampEmulatedEHSFieldUser;

  const { tenantId } = useParams<{ tenantId: string }>();

  let link = "";
  if (type === "Chemical") {
    link = `/o/${tenantId}/chemicals/catalog/chemicals/${id}`;
  } else {
    link = `/o/${tenantId}/chemicals/products/${id}`;
  }

  const shouldIgnoreIssues = ignoredIssues?.some(
    (ignoredIssue) =>
      ignoredIssue.modelId === id || issues.some((issue) => issue.modelId === ignoredIssue.modelId)
  );

  const issueCount = issues.filter(
    (issue) =>
      modelToSchemas[type].includes(issue.modelName) &&
      (issue.modelId === id || anySegment(issue, (s) => s.id === id))
  ).length;

  // Determine what content to show based on user role and issue count
  const getLinkContent = useCallback(() => {
    let shouldShowIssuesLink = false;
    let shouldShowCatalogLink = false;
    if (isEHSFieldUser) {
      if (issueCount > 0 && !shouldIgnoreIssues) {
        shouldShowIssuesLink = true;
      }
    } else {
      if (issueCount > 0) {
        shouldShowIssuesLink = true;
      } else {
        shouldShowCatalogLink = true;
      }
    }

    if (shouldShowIssuesLink) {
      return (
        <>
          Fix&nbsp;
          <IssueCount color="info" issueCount={issueCount} ignoreForReport={shouldIgnoreIssues} />
          &nbsp;
          {pluralize("issue", issueCount)} in the {type.toLowerCase()} catalog for{" "}
          <Link
            to={link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", overflow: "hidden" }}
          >
            <Typography noWrap>{name}</Typography>
          </Link>{" "}
        </>
      );
    }

    if (shouldShowCatalogLink) {
      return (
        <>
          <MenuBook fontSize="small" sx={{ mr: 1 }} />
          View the {type.toLowerCase()} catalog entry for{" "}
          <Link
            to={link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", overflow: "hidden" }}
          >
            <Typography noWrap>{name}</Typography>
          </Link>{" "}
        </>
      );
    }

    return null;
  }, [isEHSFieldUser, issueCount, shouldIgnoreIssues, link, type, name]);

  const linkContent = getLinkContent();

  if (
    !name ||
    authorizationLoading ||
    (!hasChemicalCatalogPermission && !isEHSFieldUser) ||
    !linkContent
  ) {
    return null;
  }

  return (
    <BaseLink
      linkContent={linkContent}
      link={link}
      issueCount={issueCount}
      hasIgnoredIssues={shouldIgnoreIssues}
    />
  );
};
